import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth/AuthProvider';
import { useEffectiveSupplierOrg } from '../supplierOrganizations/MimicMode';
import {
  ensureProjectFolder,
  withDriveToken,
  shareFileWithLink,
  uploadFileToDrive,
} from '../files/drive';
import {
  addReviewMember,
  addSamplePhoto,
  approveReview,
  createReviewComment,
  createSubmission,
  deleteReview,
  deleteReviewComment,
  fetchReviewComments,
  fetchReviewForTask,
  fetchReviewMembers,
  fetchReviewPeople,
  fetchReviewStatuses,
  fetchSamplePhotos,
  fetchSubmissions,
  fetchSupplierCandidates,
  fetchSupplierVisibleFiles,
  fetchVisibleReviews,
  openSupplierReview,
  removeReviewMember,
  reopenReview,
  submitSubmission,
  updateReview,
  updateReviewCommentBody,
} from './queries';
import type { ApproveReviewInput, OpenReviewInput, SupplierReview } from './types';

/**
 * Server state for the supplier review branch. Statuses are global and
 * near-static; everything else is keyed by review (or task, for the lookup
 * that decides whether a panel shows a review at all). No Realtime — the
 * branch is roughly a tenth of usage (handoff 2) and polling stays with the
 * screens that need it.
 */

export const reviewKeys = {
  all: ['supplierReviews'] as const,
  statuses: () => [...reviewKeys.all, 'statuses'] as const,
  forTask: (taskId: string) => [...reviewKeys.all, 'task', taskId] as const,
  visible: () => [...reviewKeys.all, 'visible'] as const,
  members: (reviewId: string) => [...reviewKeys.all, 'members', reviewId] as const,
  people: (reviewId: string) => [...reviewKeys.all, 'people', reviewId] as const,
  candidates: (orgId: string) => [...reviewKeys.all, 'candidates', orgId] as const,
  submissions: (reviewId: string) => [...reviewKeys.all, 'submissions', reviewId] as const,
  photos: (reviewId: string) => [...reviewKeys.all, 'photos', reviewId] as const,
  comments: (reviewId: string) => [...reviewKeys.all, 'comments', reviewId] as const,
  sharedFiles: () => [...reviewKeys.all, 'sharedFiles'] as const,
};

function useActorId(): string {
  const { access } = useAuth();
  return access.status === 'approved' ? access.profile.id : '';
}

export function useReviewStatuses() {
  const { client } = useAuth();
  return useQuery({
    queryKey: reviewKeys.statuses(),
    queryFn: () => fetchReviewStatuses(client),
    staleTime: 5 * 60 * 1000,
  });
}

export function useReviewForTask(taskId: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: reviewKeys.forTask(taskId ?? ''),
    queryFn: () => fetchReviewForTask(client, taskId as string),
    enabled: Boolean(taskId),
    retry: false,
  });
}

/** The portal's working set: every review the caller may see. */
export function useVisibleReviews() {
  const { client } = useAuth();
  // Carried, not inferred. Keyed by organization too: switching orgs in Mimic
  // Mode on the same screen would otherwise hand the new org the old one's
  // cached rows.
  const organizationId = useEffectiveSupplierOrg();
  return useQuery({
    queryKey: [...reviewKeys.visible(), organizationId],
    queryFn: () => fetchVisibleReviews(client, organizationId),
    retry: false,
  });
}

export function useReviewMembers(reviewId: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: reviewKeys.members(reviewId ?? ''),
    queryFn: () => fetchReviewMembers(client, reviewId as string),
    enabled: Boolean(reviewId),
    retry: false,
  });
}

export function useReviewPeople(reviewId: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: reviewKeys.people(reviewId ?? ''),
    queryFn: () => fetchReviewPeople(client, reviewId as string),
    enabled: Boolean(reviewId),
    retry: false,
  });
}

export function useSupplierCandidates(organizationId: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: reviewKeys.candidates(organizationId ?? ''),
    queryFn: () => fetchSupplierCandidates(client, organizationId as string),
    enabled: Boolean(organizationId),
    retry: false,
  });
}

export function useSubmissions(reviewId: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: reviewKeys.submissions(reviewId ?? ''),
    queryFn: () => fetchSubmissions(client, reviewId as string),
    enabled: Boolean(reviewId),
    retry: false,
  });
}

export function useSamplePhotos(reviewId: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: reviewKeys.photos(reviewId ?? ''),
    queryFn: () => fetchSamplePhotos(client, reviewId as string),
    enabled: Boolean(reviewId),
    retry: false,
  });
}

export function useReviewComments(reviewId: string | undefined) {
  const { client } = useAuth();
  return useQuery({
    queryKey: reviewKeys.comments(reviewId ?? ''),
    queryFn: () => fetchReviewComments(client, reviewId as string),
    enabled: Boolean(reviewId),
    retry: false,
  });
}

/** The portal's Files for Review tab: every shared file the caller can see. */
export function useSupplierVisibleFiles() {
  const { client } = useAuth();
  return useQuery({
    queryKey: reviewKeys.sharedFiles(),
    queryFn: () => fetchSupplierVisibleFiles(client),
    retry: false,
  });
}

/** Invalidates everything hanging off one review, plus the task lookup. */
function useInvalidateReview() {
  const queryClient = useQueryClient();
  return (review: Pick<SupplierReview, 'id' | 'task_id'>) => {
    void queryClient.invalidateQueries({ queryKey: reviewKeys.forTask(review.task_id) });
    void queryClient.invalidateQueries({ queryKey: reviewKeys.visible() });
    void queryClient.invalidateQueries({ queryKey: reviewKeys.members(review.id) });
    void queryClient.invalidateQueries({ queryKey: reviewKeys.people(review.id) });
    void queryClient.invalidateQueries({ queryKey: reviewKeys.submissions(review.id) });
    void queryClient.invalidateQueries({ queryKey: reviewKeys.photos(review.id) });
    void queryClient.invalidateQueries({ queryKey: reviewKeys.comments(review.id) });
  };
}

export function useOpenReview() {
  const { client } = useAuth();
  const invalidate = useInvalidateReview();
  const openedBy = useActorId();
  return useMutation({
    mutationFn: (input: Omit<OpenReviewInput, 'openedBy'>) =>
      openSupplierReview(client, { ...input, openedBy }),
    onSuccess: (review) => invalidate(review),
  });
}

export function useUpdateReview() {
  const { client } = useAuth();
  const invalidate = useInvalidateReview();
  return useMutation({
    mutationFn: ({
      reviewId,
      changes,
    }: {
      reviewId: string;
      changes: Parameters<typeof updateReview>[2];
    }) => updateReview(client, reviewId, changes),
    onSuccess: (review) => invalidate(review),
  });
}

export function useApproveReview() {
  const { client } = useAuth();
  const invalidate = useInvalidateReview();
  const approvedBy = useActorId();
  return useMutation({
    mutationFn: (input: Omit<ApproveReviewInput, 'approvedBy'>) =>
      approveReview(client, { ...input, approvedBy }),
    onSuccess: (review) => invalidate(review),
  });
}

export function useReopenReview() {
  const { client } = useAuth();
  const invalidate = useInvalidateReview();
  return useMutation({
    mutationFn: ({ reviewId, nextStatusId }: { reviewId: string; nextStatusId: string }) =>
      reopenReview(client, reviewId, nextStatusId),
    onSuccess: (review) => invalidate(review),
  });
}

export function useDeleteReview() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (reviewId: string) => deleteReview(client, reviewId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.all });
    },
  });
}

export function useAddReviewMember() {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const addedBy = useActorId();
  return useMutation({
    mutationFn: (input: { reviewId: string; supplierOrganizationId: string; userId: string }) =>
      addReviewMember(client, { ...input, addedBy }),
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.members(input.reviewId) });
      void queryClient.invalidateQueries({ queryKey: reviewKeys.people(input.reviewId) });
    },
  });
}

export function useRemoveReviewMember(reviewId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (memberId: string) => removeReviewMember(client, memberId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.members(reviewId) });
      void queryClient.invalidateQueries({ queryKey: reviewKeys.people(reviewId) });
    },
  });
}

export function useCreateSubmission(reviewId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const createdBy = useActorId();
  return useMutation({
    mutationFn: (note: string) => createSubmission(client, { reviewId, note, createdBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.submissions(reviewId) });
    },
  });
}

export function useAddSamplePhoto(reviewId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const uploadedBy = useActorId();
  return useMutation({
    mutationFn: (
      input: Omit<Parameters<typeof addSamplePhoto>[1], 'uploadedBy' | 'reviewId'> & {
        submissionId: string;
      },
    ) => addSamplePhoto(client, { ...input, reviewId, uploadedBy }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.photos(reviewId) });
    },
  });
}

/**
 * The real photo upload (handoff 36: "Upload actual sample photos"). The
 * file goes to the supplier's OWN Google Drive — never TAP's shared folder,
 * which would mean granting suppliers access to everything in it — into a
 * "TAP Sample Photos" folder created on first use. The file is then made
 * viewable by anyone with the link so the TAP side can open it, and the
 * photo row records the resulting link like a pasted one would.
 */
const SAMPLE_FOLDER_NAME = 'TAP Sample Photos';

export function useUploadSamplePhoto(reviewId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const uploadedBy = useActorId();
  return useMutation({
    mutationFn: async ({
      submissionId,
      label,
      file,
    }: {
      submissionId: string;
      label: string;
      file: File;
    }) => {
      const uploaded = await withDriveToken(client, async (token) => {
        // 'root' is Drive's alias for the signed-in person's My Drive.
        const folderId = await ensureProjectFolder(token, 'root', SAMPLE_FOLDER_NAME);
        const file_ = await uploadFileToDrive(token, folderId, file);
        await shareFileWithLink(token, file_.id);
        return file_;
      });
      return addSamplePhoto(client, {
        submissionId,
        reviewId,
        label,
        name: uploaded.name,
        driveFileId: uploaded.id,
        url: uploaded.webViewLink ?? `https://drive.google.com/file/d/${uploaded.id}/view`,
        mimeType: uploaded.mimeType,
        sizeBytes: uploaded.size === undefined ? null : Number(uploaded.size),
        uploadedBy,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.photos(reviewId) });
    },
  });
}

export function useSubmitSubmission(reviewId: string, taskId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (submissionId: string) => submitSubmission(client, submissionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.submissions(reviewId) });
      // Submitting moves the review's workflow status (database trigger).
      void queryClient.invalidateQueries({ queryKey: reviewKeys.forTask(taskId) });
      void queryClient.invalidateQueries({ queryKey: reviewKeys.visible() });
    },
  });
}

export function useCreateReviewComment(reviewId: string, projectId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const authorId = useActorId();
  return useMutation({
    mutationFn: (body: string) =>
      createReviewComment(client, { reviewId, projectId, authorId, body }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.comments(reviewId) });
      void queryClient.invalidateQueries({ queryKey: reviewKeys.people(reviewId) });
    },
  });
}

export function useUpdateReviewComment(reviewId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, body }: { commentId: string; body: string }) =>
      updateReviewCommentBody(client, commentId, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.comments(reviewId) });
    },
  });
}

export function useDeleteReviewComment(reviewId: string) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) => deleteReviewComment(client, commentId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: reviewKeys.comments(reviewId) });
    },
  });
}
